import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Expand,
  Minimize2,
} from "lucide-react";
import { marked } from "marked";
import { MermaidRenderer } from "./MermaidRenderer";
import { LazyImage } from "./LazyImage";

// Configure marked for better output
marked.setOptions({
  gfm: true,
  breaks: true,
});

/**
 * Detects if content is Markdown (vs HTML)
 */
function isMarkdown(content: string): boolean {
  if (!content) return false;

  // If it starts with HTML tags, it's probably HTML
  if (content.trim().startsWith("<")) return false;

  // Check for common markdown patterns
  const markdownPatterns = [
    /^#{1,6}\s/m, // Headers: # Title
    /\*\*[^*]+\*\*/, // Bold: **text**
    /\*[^*]+\*/, // Italic: *text*
    /```[\s\S]*?```/, // Code blocks
    /^\s*[-*+]\s/m, // Unordered lists
    /^\s*\d+\.\s/m, // Ordered lists
    /\[.+\]\(.+\)/, // Links: [text](url)
    /^>\s/m, // Blockquotes
  ];

  return markdownPatterns.some((pattern) => pattern.test(content));
}

interface Section {
  id: string;
  title: string;
  content: string;
  level: number;
}

type DisplayMode = "collapsible" | "slide";

interface InteractiveContentProps {
  html: string;
  defaultExpanded?: boolean;
  mode?: DisplayMode;
  initialSlide?: number;
  onSlideChange?: (index: number) => void;
}

/**
 * Renders HTML content with collapsible sections, slide mode, and Mermaid diagram support.
 * Automatically converts Markdown to HTML if needed.
 *
 * Modes:
 * - "collapsible": Traditional expandable sections (default)
 * - "slide": One section at a time with navigation controls
 */
export function InteractiveContent({
  html,
  defaultExpanded = true,
  mode = "collapsible",
  initialSlide = 0,
  onSlideChange,
}: InteractiveContentProps) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [slideDirection, setSlideDirection] = useState<"left" | "right">(
    "right",
  );
  const [isTransitioning, setIsTransitioning] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  // Convert Markdown to HTML if needed
  const processedHtml = useMemo(() => {
    if (!html) return "";
    if (isMarkdown(html)) {
      return marked.parse(html) as string;
    }
    return html;
  }, [html]);

  // Parse HTML into sections based on headers
  const sections = useMemo(() => {
    if (!processedHtml) return [];

    // Create a temporary div to parse HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(processedHtml, "text/html");
    const elements = Array.from(doc.body.children);

    const result: Section[] = [];
    let currentSection: Section | null = null;
    let introContent = "";

    elements.forEach((el, index) => {
      const tagName = el.tagName.toLowerCase();

      if (tagName === "h2" || tagName === "h3") {
        // Save previous section
        if (currentSection) {
          result.push(currentSection);
        } else if (introContent.trim()) {
          // Save intro content before first header
          result.push({
            id: "intro",
            title: "Introduccion",
            content: introContent,
            level: 2,
          });
        }

        // Start new section
        currentSection = {
          id: `section-${index}`,
          title: el.textContent || `Seccion ${index + 1}`,
          content: "",
          level: tagName === "h2" ? 2 : 3,
        };
      } else if (currentSection) {
        currentSection.content += el.outerHTML;
      } else {
        introContent += el.outerHTML;
      }
    });

    // Don't forget the last section
    if (currentSection) {
      result.push(currentSection);
    } else if (introContent.trim() && result.length === 0) {
      // If there are no headers, treat all content as a single section
      result.push({
        id: "content",
        title: "Contenido",
        content: introContent,
        level: 2,
      });
    }

    return result;
  }, [processedHtml]);

  // Expand all sections by default
  useEffect(() => {
    if (defaultExpanded && sections.length > 0) {
      setExpandedSections(new Set(sections.map((s) => s.id)));
    }
  }, [sections, defaultExpanded]);

  // Clamp currentSlide when sections change
  useEffect(() => {
    if (currentSlide >= sections.length && sections.length > 0) {
      setCurrentSlide(sections.length - 1);
    }
  }, [sections.length, currentSlide]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const expandAll = () => {
    setExpandedSections(new Set(sections.map((s) => s.id)));
  };

  const collapseAll = () => {
    setExpandedSections(new Set());
  };

  // Slide navigation with smooth transitions
  const goToSlide = useCallback(
    (index: number, direction?: "left" | "right") => {
      if (index < 0 || index >= sections.length || isTransitioning) return;

      const dir = direction || (index > currentSlide ? "right" : "left");
      setSlideDirection(dir);
      setIsTransitioning(true);

      // Start exit animation
      setTimeout(() => {
        setCurrentSlide(index);
        onSlideChange?.(index);

        // Scroll to top
        contentRef.current?.scrollTo({ top: 0, behavior: "smooth" });

        // End transition
        setTimeout(() => {
          setIsTransitioning(false);
        }, 300);
      }, 150);
    },
    [currentSlide, sections.length, isTransitioning, onSlideChange],
  );

  const goToPrev = useCallback(() => {
    goToSlide(currentSlide - 1, "left");
  }, [currentSlide, goToSlide]);

  const goToNext = useCallback(() => {
    goToSlide(currentSlide + 1, "right");
  }, [currentSlide, goToSlide]);

  // Keyboard navigation for slide mode
  useEffect(() => {
    if (mode !== "slide") return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPrev();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [mode, goToPrev, goToNext]);

  // If there's only one section or no headers, render without collapsible behavior
  if (sections.length <= 1) {
    return (
      <div className="interactive-content">
        <ContentRenderer html={processedHtml} />
      </div>
    );
  }

  // Slide mode rendering
  if (mode === "slide") {
    const currentSection = sections[currentSlide];

    return (
      <div className="interactive-content-slide">
        {/* Progress indicator */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 transition-all duration-300 ease-out"
              style={{
                width: `${((currentSlide + 1) / sections.length) * 100}%`,
              }}
            />
          </div>
          <span className="text-sm text-gray-500 font-medium tabular-nums">
            {currentSlide + 1} / {sections.length}
          </span>
        </div>

        {/* Section title */}
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          {currentSection?.title}
        </h2>

        {/* Content with transitions */}
        <div
          ref={contentRef}
          className="relative overflow-hidden min-h-[200px]"
        >
          <div
            className={`transition-all duration-300 ease-out ${
              isTransitioning
                ? slideDirection === "right"
                  ? "-translate-x-4 opacity-0"
                  : "translate-x-4 opacity-0"
                : "translate-x-0 opacity-100"
            }`}
          >
            {currentSection && (
              <ContentRenderer html={currentSection.content} />
            )}
          </div>
        </div>

        {/* Navigation controls */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={goToPrev}
            disabled={currentSlide === 0 || isTransitioning}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </button>

          {/* Dot indicators */}
          <div className="hidden sm:flex items-center gap-1.5">
            {sections.map((_, index) => (
              <button
                key={index}
                type="button"
                onClick={() => goToSlide(index)}
                disabled={isTransitioning}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentSlide
                    ? "bg-primary-500 scale-110"
                    : "bg-gray-200 hover:bg-gray-300"
                }`}
                aria-label={`Ir a seccion ${index + 1}`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={goToNext}
            disabled={currentSlide === sections.length - 1 || isTransitioning}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            Siguiente
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  // Collapsible mode rendering (default)
  return (
    <div className="interactive-content">
      {/* Global controls */}
      <div className="flex items-center justify-end gap-2 mb-4 text-sm">
        <button
          type="button"
          onClick={expandAll}
          className="flex items-center gap-1 px-2 py-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
        >
          <Expand className="h-3.5 w-3.5" />
          Expandir todo
        </button>
        <span className="text-gray-300">|</span>
        <button
          type="button"
          onClick={collapseAll}
          className="flex items-center gap-1 px-2 py-1 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded transition-colors"
        >
          <Minimize2 className="h-3.5 w-3.5" />
          Colapsar todo
        </button>
      </div>

      {/* Sections */}
      <div className="space-y-2">
        {sections.map((section) => (
          <div
            key={section.id}
            className={`border rounded-lg overflow-hidden transition-colors ${
              section.level === 2
                ? "border-gray-200 bg-white"
                : "border-gray-100 bg-gray-50/50 ml-4"
            }`}
          >
            {/* Section header */}
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className={`w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors text-left ${
                expandedSections.has(section.id)
                  ? "border-b border-gray-100"
                  : ""
              }`}
            >
              <span
                className={`font-medium ${
                  section.level === 2
                    ? "text-lg text-gray-900"
                    : "text-base text-gray-700"
                }`}
              >
                {section.title}
              </span>
              {expandedSections.has(section.id) ? (
                <ChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0" />
              )}
            </button>

            {/* Section content with animation */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-out ${
                expandedSections.has(section.id)
                  ? "max-h-[5000px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="px-4 pb-4 pt-2">
                <ContentRenderer html={section.content} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Renders HTML content with Mermaid diagram support and lazy loading images
 */
function ContentRenderer({ html }: { html: string }) {
  const { processedHtml, mermaidBlocks, imageBlocks } = useMemo(() => {
    if (!html) return { processedHtml: "", mermaidBlocks: [], imageBlocks: [] };

    const mermaidArr: string[] = [];
    const imageArr: { src: string; alt: string }[] = [];

    // Pattern to match Mermaid code blocks (both formats)
    const mermaidPatterns = [
      // <pre data-type="mermaid">code</pre>
      /<pre[^>]*data-type="mermaid"[^>]*>([\s\S]*?)<\/pre>/gi,
      // <pre><code class="language-mermaid">code</code></pre>
      /<pre[^>]*>\s*<code[^>]*class="language-mermaid"[^>]*>([\s\S]*?)<\/code>\s*<\/pre>/gi,
    ];

    // Pattern to match images for lazy loading
    const imagePattern = /<img\s+([^>]*?)>/gi;

    let processed = html;

    // Process Mermaid blocks
    mermaidPatterns.forEach((pattern) => {
      processed = processed.replace(pattern, (_match, code) => {
        const index = mermaidArr.length;
        // Clean up the code
        const cleanCode = code
          .replace(/<[^>]*>/g, "") // Remove any nested HTML tags
          .trim();
        mermaidArr.push(cleanCode);
        return `<div data-mermaid-placeholder="${index}"></div>`;
      });
    });

    // Process images for lazy loading
    processed = processed.replace(imagePattern, (match, attrs) => {
      const srcMatch = attrs.match(/src=["']([^"']+)["']/i);
      const altMatch = attrs.match(/alt=["']([^"']*)["']/i);

      if (srcMatch) {
        const index = imageArr.length;
        imageArr.push({
          src: srcMatch[1],
          alt: altMatch ? altMatch[1] : "",
        });
        return `<div data-lazy-image-placeholder="${index}"></div>`;
      }
      return match;
    });

    return {
      processedHtml: processed,
      mermaidBlocks: mermaidArr,
      imageBlocks: imageArr,
    };
  }, [html]);

  // If there are no special blocks, just render the HTML
  if (mermaidBlocks.length === 0 && imageBlocks.length === 0) {
    return (
      <div
        className="prose prose-sm sm:prose max-w-none prose-headings:font-semibold prose-a:text-primary-600 prose-code:text-primary-700 prose-code:bg-primary-50 prose-code:px-1 prose-code:rounded prose-img:rounded-lg prose-img:shadow-sm"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  // Split content by placeholders and render with components
  const parts = processedHtml.split(
    /(<div data-(?:mermaid|lazy-image)-placeholder="\d+"><\/div>)/,
  );

  return (
    <div className="prose prose-sm sm:prose max-w-none prose-headings:font-semibold prose-a:text-primary-600 prose-code:text-primary-700 prose-code:bg-primary-50 prose-code:px-1 prose-code:rounded prose-img:rounded-lg prose-img:shadow-sm">
      {parts.map((part, index) => {
        // Check for Mermaid placeholder
        const mermaidMatch = part.match(/data-mermaid-placeholder="(\d+)"/);
        if (mermaidMatch) {
          const mermaidIndex = parseInt(mermaidMatch[1] ?? "0", 10);
          const code = mermaidBlocks[mermaidIndex];
          if (code) {
            return <MermaidRenderer key={`mermaid-${index}`} code={code} />;
          }
          return null;
        }

        // Check for lazy image placeholder
        const imageMatch = part.match(/data-lazy-image-placeholder="(\d+)"/);
        if (imageMatch) {
          const imageIndex = parseInt(imageMatch[1] ?? "0", 10);
          const image = imageBlocks[imageIndex];
          if (image) {
            return (
              <LazyImage
                key={`image-${index}`}
                src={image.src}
                alt={image.alt}
                className="rounded-lg shadow-sm max-w-full"
                objectFit="contain"
                blurPlaceholder
              />
            );
          }
          return null;
        }

        if (part.trim()) {
          return (
            <div
              key={`content-${index}`}
              dangerouslySetInnerHTML={{ __html: part }}
            />
          );
        }

        return null;
      })}
    </div>
  );
}
